import { MenuItem, Select } from '@mui/material';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { IconButton } from '@design-system/components/button/iconButton';
import { Input } from '@design-system/components/input/input';
import { InputNumber } from '@design-system/components/input/inputNumber';
import { bgColorsVars } from '@design-system/vars.css';
import { ToggleButtonGroup } from '@client/new/components/ToggleButtonGroup/ToggleButtonGroup';
import { Option } from '@client/new/helpers/types';
import type {
  Set,
  SetMode,
} from '@client/new/pages/workout/models/workout.domain';
import * as styles from './SetDoseCard.css';

export type SetDose = Pick<Set, 'reps' | 'mode' | 'repsMax'>;

export type SetDoseErrors = Partial<
  Record<'reps' | 'repsMax', { message?: string }>
>;

const setUnits = ['reps', 'sec', 'min', 'meters', 'calories'];

const setModeOptions: Option<SetMode>[] = [
  { label: 'Fixed', value: 'fixed' },
  { label: 'Range', value: 'range' },
  { label: 'Minimum', value: 'minimum' },
  { label: 'To Failure', value: 'toFailure' },
];

// Mirrors the design-system input frame (1px N-40 border, 10px radius) with a
// light grey rest state and a black selected segment, as in the Figma.
const setModeGroupSx = {
  width: '100%',
  border: 'solid 1px',
  borderColor: bgColorsVars['N-40'],
  borderRadius: '10px',
  backgroundColor: bgColorsVars['N-10'],
  overflow: 'hidden',
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: 0,
    marginLeft: 0,
  },
  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    borderLeft: 'solid 1px',
    borderLeftColor: bgColorsVars['N-40'],
  },
};

const setModeButtonSx = {
  flex: 1,
  padding: '8px 16px',
  fontFamily: 'inherit',
  fontWeight: 500,
  textTransform: 'none',
  color: bgColorsVars['N-100'],
  backgroundColor: bgColorsVars['N-10'],
  '&:hover': {
    backgroundColor: bgColorsVars['N-20'],
    color: bgColorsVars['N-100'],
  },
  '&.Mui-selected': {
    color: bgColorsVars['W-100'],
    backgroundColor: bgColorsVars['N-100'],
  },
  '&.Mui-selected:hover': { backgroundColor: bgColorsVars['N-90'] },
};

const unitSelectSx = {
  minWidth: '130px',
  height: 44,
  padding: '8px 16px',
  border: 'solid 2px',
  borderRadius: '10px',
  borderColor: bgColorsVars['N-40'],
  backgroundColor: bgColorsVars['W-100'],
  boxShadow: 'none',
  fontWeight: 500,
  fontFamily: 'inherit',
  margin: '1px',
  '&.Mui-focused': {
    margnin: 0,
    boxShadow: 'none',
    borderWidth: '3px',
  },
};

// The unit belongs to the whole exercise: the apps read it from the first
// set, so a unit pick goes through onUnitChange to every set at once.
export const SetDoseCard = <T extends SetDose>({
  label,
  set,
  onChange,
  onUnitChange,
  onRemove,
  errors = {},
  disabled,
  readonly,
}: {
  label: string;
  set: T;
  onChange: (set: T) => void;
  onUnitChange: (unit: string) => void;
  onRemove?: () => void;
  errors?: SetDoseErrors;
  disabled?: boolean;
  readonly?: boolean;
}) => {
  const mode: SetMode = set.mode ?? 'fixed';
  const [amount, unit = 'reps'] = set.reps;
  const update = (patch: Partial<SetDose>) => onChange({ ...set, ...patch });
  const changeMode = (next: SetMode) =>
    update({
      mode: next,
      repsMax: next === 'range' ? (set.repsMax ?? null) : undefined,
      reps: mode === 'toFailure' && next !== 'toFailure' ? [0, unit] : set.reps,
    });

  return (
    <div className={styles.setCard} data-testid="set-card">
      <div className="flex items-center justify-between">
        <Typography fontWeight="bold">{label}</Typography>
        {onRemove && (
          <IconButton
            variant="text"
            color="neutral"
            icon={faClose}
            iconClassName="font-normal text-[20px]"
            disabled={readonly}
            onClick={onRemove}
          />
        )}
      </div>

      <ToggleButtonGroup<SetMode>
        value={mode}
        options={setModeOptions}
        toStringValue={(v) => v}
        toValue={(v) => v as SetMode}
        onChange={(_e, next) => changeMode(next)}
        groupSx={setModeGroupSx}
        buttonSx={setModeButtonSx}
      />

      {mode === 'toFailure' ? (
        <Input
          value="Performs as many reps as possible"
          disabled
          readOnly
          className="text-center"
        />
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <InputNumber
              disabled={disabled}
              value={amount as number}
              onChange={(val) => update({ reps: [val ?? 0, unit] })}
              error={errors.reps?.message}
              min={0}
              wrapperClassName={styles.setAmountInput}
              endCta={mode === 'minimum' ? <Typography>+</Typography> : null}
            />
            {mode === 'range' && (
              <>
                <Typography>-</Typography>
                <InputNumber
                  disabled={disabled}
                  value={set.repsMax as number}
                  onChange={(val) => update({ repsMax: val })}
                  error={errors.repsMax?.message}
                  min={0}
                  wrapperClassName={styles.setAmountInput}
                />
              </>
            )}
          </div>

          <Select
            value={unit}
            className="w-[120px]"
            defaultValue={'min'}
            sx={unitSelectSx}
            disabled={readonly}
          >
            {setUnits.map((option) => (
              <MenuItem
                key={option}
                value={option}
                onClick={() => onUnitChange(option)}
              >
                <Typography variant="body" size="large">
                  {option}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
};
